// ============================================================================
// SYUT ENGAGEMENT SELECT
// ============================================================================
// Domain wrapper around SyutSelect for the Engagement master.
// Label format: "Client Name - Engagement Type - Year (CODE)"
// Searchable server-side across client name, engagement type name, year, and code
// (the backend searchTerm already matches across these fields).
//
// Defaults to excluding CLOSED engagements via `onlyOpen: true`. Override with
// `onlyOpen={false}` to show all.

import React, { useCallback } from 'react';
import { SyutSelect } from '../SyutSelect';
import { engagementService } from 'api/services/engagementService';
import { useResolvedSeedOptions } from './useResolvedSeedOptions';

/** One place that builds an engagement's label, so the dropdown and the
 *  resolved selected value can never disagree about how one is written. */
const engagementLabel = (e: any) => {
  const parts = [e.clientName, e.engagementTypeName, e.year].filter(Boolean);
  const base = parts.join(' - ');
  return e.code ? `${base} (${e.code})` : base;
};

export interface SyutEngagementSelectProps {
  value?: string;
  onChange: (value: string) => void;
  label?: React.ReactNode;
  error?: string;
  required?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  seedOptions?: { value: string; label: string }[];
  /** If true (default), exclude engagements with status CLOSED. */
  onlyOpen?: boolean;
  includeInactive?: boolean;
  /** Rows per server query (default: 500). */
  pageSize?: number;
  disabled?: boolean;
}

export const SyutEngagementSelect: React.FC<SyutEngagementSelectProps> = ({
  value,
  onChange,
  label,
  error,
  required,
  isClearable = true,
  placeholder = 'Type 3 letters to search by client, type, or year...',
  size = 'sm',
  seedOptions,
  onlyOpen = true,
  includeInactive = false,
  pageSize = 500,
  disabled,
}) => {
  // A selected engagement outside the loaded page would otherwise render blank
  // — see useResolvedSeedOptions for the full account.
  //
  // This one bites soonest of all the wrappers: engagements are transactional,
  // so the row count grows every year rather than sitting at a master's size,
  // and `onlyOpen` narrows the loaded page further. A timesheet or disbursement
  // referencing an engagement CLOSED since it was booked must still show which
  // engagement that was — the lookup deliberately ignores onlyOpen, while the
  // picker keeps filtering what it OFFERS.
  const fetchOption = useCallback(async (id: string) => {
    const res = await engagementService.getById(id);
    const e: any = res?.data;
    return e ? { value: String(e.id), label: engagementLabel(e) } : null;
  }, []);

  const effectiveSeedOptions = useResolvedSeedOptions(value, seedOptions, fetchOption);

  return (
    <SyutSelect
      label={label}
      value={value}
      isAsync
      loadOptions={async (input: string) => {
        const res = await engagementService.getAll({
          page: 1,
          pageSize,
          searchTerm: input || undefined,
          onlyOpen,
          includeInactive,
        });
        return res.data.map((e: any) => ({ value: e.id, label: engagementLabel(e) }));
      }}
      defaultOptions
      cacheOptions={false}
      seedOptions={effectiveSeedOptions}
      debounceDelay={300}
      minSearchLength={3}
      isClearable={isClearable}
      size={size}
      placeholder={placeholder}
      error={error}
      required={required}
      isDisabled={disabled}
      onChange={(val) => onChange((val as string) || '')}
    />
  );
};

export default SyutEngagementSelect;
